import { Component, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subscription } from 'rxjs/index';
import { filter, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { BroadcastService } from '@services/broadcast-service';
import { ConnectionService } from '@services/connection-service';
import { AddressFormComponent } from '@shared/address-form/address-form.component';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import * as Types from '@store/modals';
import { fromSelectors } from '@store/selectors';
import { fromActions } from '@store/actions';
import { CurrentComponentService } from '@services/current-component';
import { AppWebBridgeService } from '@services/app-web-bridge-service';
import { WindowRefService } from '@services/window-ref-service';
import { AddressBookI } from '@store/modals';
import { Table } from '@cureskin/api-client';
import { EventLoggerService } from '@services/event-logger-service';
import { UserProfileEditComponent } from '../../user-profile/user-profile-edit/user-profile-edit.component';
import { AppConfig } from 'client/app/app.config';
import { UserAddressBottomsheetComponent } from '../user-address-bottomsheet/user-address-bottomsheet.component';
import { ShiprocketService } from '@services/shiprocket-service';
import { AddressBottomsheetMode } from 'client/app/app.constants';

const SHIPROCKET_OTP_LENGTH = 6;
const SHIPROCKET_RESEND_SECONDS = 30;

@Component({
  selector: 'user-address-edit',
  templateUrl: './user-address-edit.html',
  styleUrls: ['./user-address-edit.scss'],
  standalone: false,
})
export class UserAddressEditComponent implements OnDestroy {
  address: any;
  addressForm: AddressBookI;
  user: any;
  allowedAccess: boolean;
  popUpModal: any = {};
  saveInProcess$: Observable<boolean> = this.store.select(fromSelectors.selectAddressSaveInProcess);
  isEdit: boolean;
  loading: boolean = true;
  isLocatingAddress: boolean;
  subscriptions: Subscription[] = [];
  experiments: Array<any> = [];
  autoLocationPermissionEnabled: boolean = false;
  autoFillLocationEnabled: boolean = false;
  hideLocation: boolean = false;
  isAddressAutofilled: boolean = false;
  isAddressEditedAfterAutofilling: boolean = false;
  shouldEnableUseCurrentLocationButton: boolean = false;
  startTimer:number;
  acqRebranding2025: boolean = false;
  prefillAddressShiprocketExperiment: boolean = false;
  shiprocketSticky: boolean = true;
  showShiprocketBanner: boolean = true;
  shiprocketAddressExists: boolean = false;
  shiprocketAddressesCount: number = 0;
  shiprocketPhone: string = '';
  private isHandlingShiprocketFlowEditSave: boolean = false;
  shouldShowShiprocketBannerNonSticky: boolean = false;
  shouldShowShiprocketBannerSticky: boolean = false;
  isKeyboardVisible: boolean = false;
  private hasSentBannerImpression: boolean = false;
  private bannerObserver: IntersectionObserver | null = null;

  @ViewChild(AddressFormComponent) addressFormComponent: AddressFormComponent;
  @ViewChild(UserProfileEditComponent) profileEdit: UserProfileEditComponent;
  @ViewChild('shiprocketBannerNonSticky', { read: ElementRef }) shiprocketBannerNonSticky: ElementRef;
  @ViewChild('shiprocketBannerSticky', { read: ElementRef }) shiprocketBannerSticky: ElementRef;

  constructor(
    private conn: ConnectionService,
    private broadcast: BroadcastService,
    private route: ActivatedRoute,
    private store: Store,
    private currentComponentService: CurrentComponentService,
    private appWebBridge: AppWebBridgeService,
    private readonly bottomSheet: MatBottomSheet,
    private eventLogger: EventLoggerService,
    private window: WindowRefService,
    private readonly appConfig: AppConfig,
    private shiprocketService: ShiprocketService,
  ) {
    this.currentComponentService.set(this);
  }

  canSaveAddress(): boolean {
    const addressFormComponent = this.addressFormComponent;
    const form = addressFormComponent?.addressForm;
    if (!form) return false;

    const raw: any = form.getRawValue ? form.getRawValue() : {};

    const isNonEmpty = (value: any): boolean =>
      typeof value === 'string' ? value.trim().length > 0 : value !== undefined && value !== null && `${value}`.trim().length > 0;

    const zip: string = `${raw.zipCode ?? ''}`.trim();
    const isZipValid = zip.length === 6 && !addressFormComponent.pinCodeError;

    // Required fields (as per UI): name, pincode, city, house/building, locality.
    const requiredFieldsFilled =
      isNonEmpty(raw.contactName)
      && isNonEmpty(raw.buildingDetails)
      && isNonEmpty(raw.localityDetails)
      && isNonEmpty(raw.city)
      && isZipValid;

    // Mobile number is shown/required only in checkout flow for non-internal users.
    const requiresMobileNumber =
      !!addressFormComponent.isCheckout && !addressFormComponent.internalUser;
    const mobileOk = !requiresMobileNumber
      || (isNonEmpty(raw.mobileNumber)
        && `${raw.mobileNumber}`.trim().length === 10
        && form.controls?.mobileNumber?.valid !== false);

    const fieldErrors: any = addressFormComponent.addressFieldError || {};
    const hasCustomFieldErrors =
      !!fieldErrors?.contactName?.contactNameError
      || !!fieldErrors?.city?.cityError
      || !!fieldErrors?.buildingDetails?.buildingDetailsError
      || !!fieldErrors?.localityDetails?.localityDetailsError
      || !!fieldErrors?.landmark?.landmarkError;
    return requiredFieldsFilled
      && mobileOk
      && form.valid
      && !hasCustomFieldErrors
      && !addressFormComponent.pinCodeError;
  }

  async ngOnInit(): Promise<void> {
    this.appWebBridge.setStatusBarColor('white');
    this.startTimer = new Date().getTime();
    await this.setUserExperiments();
    this.hideLocation = !this.shouldEnableUseCurrentLocationButton;
    this.user = this.conn.getActingUser();
    const addressEditId: string = this.route.snapshot.params.id;
    this.isEdit = !!(addressEditId?.length);
    const isNewAddress: boolean = !this.isEdit;
    // Include explicit new/edit flag in pageOpen payload
    this.eventLogger.cleverTapEvent('pageOpen', JSON.stringify({ pageName: 'address-page', isNewAddress }), [
      this.appConfig.Shared.EventTracker.MIXPANEL,
      this.appConfig.Shared.EventTracker.AMPLITUDE,
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ]);
    this.appWebBridge.logEventInBranchAndFirebaseFromiOS({
      branch: { name: 'pageOpenAddressPage' },
      firebase: { name: 'pageOpenAddressPage' },
    });
    await this.fetchOrCreateAddress(addressEditId);
    this.subscriptions.push(this.broadcast.on<{ [key: string]: string }>('LOCATION_UPDATE')
      .subscribe(async (latLong: { [key: string]: string }): Promise<void> => {
        await this.getDetailedLocationFromCoordinates(latLong);
        this.isLocatingAddress = false;
      }));

    if (this.autoFillLocationEnabled && !this.address.id) {
      const latLong = this.user?.get('lastLocation');
      if (latLong) {
        this.isAddressAutofilled = true;
        this.eventLogger.cleverTapEvent('autofilled_address_from_location');
        this.broadcast.broadcast('NOTIFY', { message: 'Autofilling location' });
        await this.getDetailedLocationFromCoordinates(latLong);
      }
    }
    if (this.autoLocationPermissionEnabled) {
      this.getLocationDetails();
    }
    await this.initShiprocketPrefillIfNeeded();
    this.updateShiprocketBannerVisibility();

    // Subscribe to keyboard visibility events
    this.subscriptions.push(
      this.broadcast.on<boolean>('KEYBOARD_VISIBLE').subscribe((isVisible: boolean): void => {
        this.isKeyboardVisible = isVisible;
        this.updateShiprocketBannerVisibility();
      })
    );

    const time = new Date().getTime() - this.startTimer;
    this.eventLogger.trackEvent('address_screen_loading_time', { timeInMilliSec: time, userId: this.user.id });
    this.loading = false;
  }

  async setUserExperiments(): Promise<void> {
    this.experiments = await this.conn.findUserActiveExperiments();
    this.experiments.forEach((each: any): void => {
      if (each.key === 'auto_ask_location_permission') this.autoLocationPermissionEnabled = true;
      if (each.key === 'auto_fill_address_from_location') this.autoFillLocationEnabled = true;
      if (each.key === 'use_current_location_button_enable') this.shouldEnableUseCurrentLocationButton = true;
      if (each.key === 'acq_rebranding_2025') this.acqRebranding2025 = true;
      if (each.key === 'prefill_address_shiprocket') {
        this.prefillAddressShiprocketExperiment = true;
        this.shiprocketSticky = each?.variant?.sticky !== false;
        this.showShiprocketBanner = each?.variant?.showBanner !== false;
      }
    });
  }

  async checkShipRocketAddressExists(): Promise<void> {
    if (!this.user || !this.shiprocketService.isAcquisitionUser()) {
      this.shiprocketAddressExists = false;
      return;
    }

    try {
      const response = await this.conn.checkIfUserAddressExistsInShipRocket({
        countryCode: '91',
        phone: this.user.get('MobileNumber'),
      });
      this.shiprocketAddressExists = response?.exists ?? false;
      if (this.shiprocketAddressExists) {
        this.eventLogger.cleverTapEvent('shiprocket_address_found', JSON.stringify({ userId: this.user.id }));
      }
    } catch (error) {
      this.shiprocketAddressExists = false;
    }
  }


  selectShiprocketAddress(): void {
    const phone = this.getShiprocketPhone();
    if (!phone) return;
    this.navigateToShiprocketAddressList(phone);
  }

  private async handleShiprocketOtpRequest(mobileNumber?: string): Promise<boolean> {
    return this.shiprocketService.handleOtpRequest(mobileNumber);
  }

  async verifyShiprocketOTP(otp: string, mobileNumber: string): Promise<boolean> {
    if (!this.user) {
      return false;
    }

    return this.shiprocketService.verifyOTP(otp, mobileNumber, async (verifiedMobileNumber: string): Promise<void> => {
      this.navigateToShiprocketAddressList(verifiedMobileNumber);
    });
  }

  openShiprocketAddressBottomsheet(): void {
    this.openShiprocketBottomsheet(this.appConfig.Shared.AddressBook.BottomsheetMode.Otp);
  }

  private openShiprocketBottomsheet(mode: AddressBottomsheetMode): void {
    if (!this.user) return;

    const rawMobile = this.user.get('MobileNumber');

    this.bottomSheet.open(UserAddressBottomsheetComponent, {
      data: {
        mode,
        mobileNumber: rawMobile,
        otpLength: SHIPROCKET_OTP_LENGTH,
        resendSeconds: SHIPROCKET_RESEND_SECONDS,
        onSendOtp: (mobileNumber: string): Promise<boolean> => this.handleShiprocketOtpRequest(mobileNumber),
        onVerifyOtp: (otp: string, mobileNumber: string): Promise<boolean> => {
          return this.verifyShiprocketOTP(otp, mobileNumber);
        },
        onResendOtp: (mobileNumber: string): Promise<boolean> => this.handleShiprocketOtpRequest(mobileNumber),
      },
    });
  }

  showPopup(): void {
    this.popUpModal = {
      open: true,
    };
  }

  async fetchOrCreateAddress(addressId?: string): Promise<void> {
    if (addressId) this.address = await this.conn.fetchAddressById(addressId);
    else {
      this.address = new Table.AddressBook();
      this.address.set('user', this.user);
    }
    if (!this.address) {
      this.broadcast.broadcast('NOTIFY', { message: 'Address not found.' });
      return;
    }
    const addressForm: Types.AddressBookI = JSON.parse(JSON.stringify(this.address));
    if (!addressId) {
      addressForm.contactName = this.user?.get('PatientName');
    }
    this.addressForm = addressForm;
  }

  async saveAddress(): Promise<void> {
    this.handleAutofillLabels();
    await this.addressFormComponent.validateForm('parent');
    if (!this.addressFormComponent.addressForm.valid) return;
    if (this.addressFormComponent.pinCodeError) {
      this.showPopup();
      return;
    }

    const isShiprocketFlowEdit = this.isShiprocketFlowEdit();
    this.saveAddressToStore();
    if (isShiprocketFlowEdit) {
      this.handleShiprocketFlowEditSave();
    }
  }

  private handleAutofillLabels(): void {
    if (this.addressFormComponent.editedCount > 5 && this.isAddressAutofilled) {
      this.isAddressEditedAfterAutofilling = true;
      this.eventLogger.cleverTapEvent('edited_autofilled_address_from_location');
      this.address.addUnique('labels', 'edited_after_autofill');
      this.address.save();
    } else if (this.isAddressAutofilled) {
      this.address.addUnique('labels', 'autofilled_unedited');
      this.address.save();
    }
  }

  private isShiprocketFlowEdit(): boolean {
    const params = this.route.snapshot.queryParams || {};
    return this.isEdit && params.source === this.appConfig.Shared.Shiprocket.Source;
  }

  private saveAddressToStore(): void {
    this.currentComponentService.remove(this);
    this.addressForm.mobileNumber = String(this.addressForm.mobileNumber);
    this.store.dispatch(fromActions.AddressBookSaveBegin({
      addressId: this.address.id,
      address: this.addressForm as Types.AddressBookI,
    }));
  }

  private handleShiprocketFlowEditSave(): void {
    this.isHandlingShiprocketFlowEditSave = true;
    this.saveInProcess$.pipe(
      filter((inProcess: boolean): boolean => !inProcess),
      take(1),
    ).subscribe(async (): Promise<void> => {
      await this.updateStoreWithSavedAddress();
      this.clearShiprocketData();
      await this.navigateToCheckoutWithParams();
      this.isHandlingShiprocketFlowEditSave = false;
    });
  }

  private async updateStoreWithSavedAddress(): Promise<void> {
    const savedAddress = await this.conn.fetchAddressById(this.address.id);
    const savedAddressJson = JSON.parse(JSON.stringify(savedAddress));
    this.store.dispatch(fromActions.AddressBookUpdate({
      payload: {
        defaultAddress: savedAddressJson,
      },
    }));
  }

  private async navigateToCheckoutWithParams(): Promise<void> {
    const params = this.route.snapshot.queryParams || {};
    const checkoutUrl = this.buildUrlWithParams('/user/checkout', params);
    await this.conn.navigateToURL(checkoutUrl);
  }

  private clearShiprocketData(): void {
    this.shiprocketService.clearAllAddressData();
    this.shiprocketService.clearLoginToken();
  }

  closePopup(): void {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'no-autofill-pincode' }));
    this.popUpModal = { open: false };
    this.back();
  }

  onClickYes(): void {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'yes-autofill-pincode' }));
    this.closePopup();
    this.getLocationDetails();
  }

  getLocationDetails(): any {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'use-my-current-location' }));
    this.isLocatingAddress = true;
    if (this.appWebBridge.getLocationDetails()) return;
    this.window.nativeWindow.navigator.geolocation.getCurrentPosition((location: any): void => {
      this.getDetailedLocationFromCoordinates({ latitude: location.coords.latitude, longitude: location.coords.longitude });
    }, (): any => {
      this.isLocatingAddress = false;
      this.broadcast.broadcast('NOTIFY', { message: 'Location permission denied. Kindly enable permission in your browser settings.' });
    });
    this.allowedAccess = true;
  }

  async getDetailedLocationFromCoordinates(location: { [key: string]: string }): Promise<void> {
    const data = await this.conn.getDetailedLocationFromCoordinates(location);
    this.isLocatingAddress = false;
    let pincode: number;
    try {
      data.results.forEach((result: { [key: string]: any }): void => {
        if (result.types.includes('postal_code')) {
          result.address_components.forEach((addressComponent: { [key: string]: any }): void => {
            if (!pincode) pincode = Number(addressComponent.long_name);
          });
        }
      });
      if (pincode) this.addressFormComponent.addressForm.controls.zipCode.setValue(pincode);
      this.address.set('latLong', location);
      this.address.set('reverseGeocodeAPIResponse', data);
      if (this.address.id) {
        await this.address.save();
      }
    } catch (err) {
      this.broadcast.broadcast('NOTIFY', { message: 'Error while getting location.' });
    }
  }

  async handleBackPress(): Promise<boolean> {
    if (this.isHandlingShiprocketFlowEditSave) {
      return true;
    }

    if (this.route.snapshot.queryParams.fromCheckout) {
      this.currentComponentService.remove(this);
      this.broadcast.broadcast('NAVIGATION_BACK', { step: 2 });
      return true;
    }
    return false;
  }

  back(): void {
    this.broadcast.broadcast('NAVIGATION_BACK');
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((each: any): void => each.unsubscribe());
    this.currentComponentService.remove(this);
    if (this.bannerObserver) {
      this.bannerObserver.disconnect();
      this.bannerObserver = null;
    }
  }

  private async initShiprocketPrefillIfNeeded(): Promise<void> {
    if (!this.shouldInitShiprocketPrefill()) {
      return;
    }
    await this.checkShipRocketAddressExists();
    this.loadShiprocketDataFromStorage();
  }

  private shouldInitShiprocketPrefill(): boolean {
    return this.prefillAddressShiprocketExperiment && this.shiprocketService.isAcquisitionUser();
  }

  private loadShiprocketDataFromStorage(): void {
    const queryParams = this.route.snapshot.queryParams || {};
    const phoneFromQuery = queryParams[this.appConfig.Shared.Shiprocket.PhoneParam];
    const storedData = this.shiprocketService.getAddressData(phoneFromQuery);
    if (storedData) {
      this.shiprocketPhone = storedData.phone;
      this.shiprocketAddressesCount = storedData.addressCount;
    }
  }

  private updateShiprocketBannerVisibility(): void {
    // When keyboard is visible, hide both banners
    if (this.isKeyboardVisible) {
      this.shouldShowShiprocketBannerNonSticky = false;
      this.shouldShowShiprocketBannerSticky = false;
      return;
    }

    // When keyboard is not visible, show banner based on sticky setting
    const effectiveSticky = this.shiprocketSticky;
    this.shouldShowShiprocketBannerNonSticky = this.shiprocketAddressExists && !this.isEdit && !effectiveSticky && this.showShiprocketBanner;
    this.shouldShowShiprocketBannerSticky = this.shiprocketAddressExists && !this.isEdit && effectiveSticky && this.showShiprocketBanner;

    // Set up observer when banner becomes visible
    if ((this.shouldShowShiprocketBannerNonSticky || this.shouldShowShiprocketBannerSticky) && !this.hasSentBannerImpression) {
      setTimeout(() => {
        this.setupBannerIntersectionObserver();
      }, 100);
    }
  }

  private setupBannerIntersectionObserver(): void {
    // Don't set up if already sent impression
    if (this.hasSentBannerImpression) {
      return;
    }

    // Clean up existing observer if any
    if (this.bannerObserver) {
      this.bannerObserver.disconnect();
      this.bannerObserver = null;
    }

    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5, // Trigger when 50% of banner is visible
    };

    this.bannerObserver = new IntersectionObserver((entries: IntersectionObserverEntry[]): void => {
      entries.forEach((entry: IntersectionObserverEntry): void => {
        if (entry.isIntersecting && !this.hasSentBannerImpression) {
          this.sendShiprocketBannerImpressionEvent();
          this.hasSentBannerImpression = true;
          // Disconnect after first impression
          if (this.bannerObserver) {
            this.bannerObserver.disconnect();
            this.bannerObserver = null;
          }
        }
      });
    }, options);

    // Observe the visible banner element
    const bannerElement = this.shouldShowShiprocketBannerNonSticky
      ? this.shiprocketBannerNonSticky?.nativeElement
      : this.shouldShowShiprocketBannerSticky
        ? this.shiprocketBannerSticky?.nativeElement
        : null;

    if (bannerElement) {
      this.bannerObserver.observe(bannerElement);
    }
  }

  private sendShiprocketBannerImpressionEvent(): void {
    const bannerType = this.shouldShowShiprocketBannerSticky ? 'sticky' : 'non-sticky';
    const eventData = {
      name: 'shiprocket_address_banner',
      bannerType,
      shiprocketAddressesCount: this.shiprocketAddressesCount,
      isEdit: this.isEdit,
    };
    this.eventLogger.cleverTapEvent('impression', JSON.stringify(eventData));
  }

  private getShiprocketPhone(): string {
    const storedData = this.shiprocketService.getAddressData();
    return storedData?.phone || this.shiprocketPhone || this.user?.get('MobileNumber');
  }

  private navigateToShiprocketAddressList(phone: string): void {
    const currentParams = this.route.snapshot.queryParams || {};
    const mergedParams: Record<string, any> = {
      ...currentParams,
      source: this.appConfig.Shared.Shiprocket.Source,
      [this.appConfig.Shared.Shiprocket.PhoneParam]: phone,
    };
    const url = this.buildUrlWithParams('/user/address', mergedParams);
    this.conn.navigateToURL(url);
  }

  private buildUrlWithParams(base: string, params: Record<string, any>): string {
    const keys = Object.keys(params || {});
    if (!keys.length) return base;
    const qs = keys
      .map((key: string): string => `${encodeURIComponent(key)}=${encodeURIComponent(String(params[key]))}`)
      .join('&');
    return `${base}?${qs}`;
  }
}
